import React from 'react';
import {
  MasonryEngineNodeAction,
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import { Button } from '@leagueplatform/genesis-core';
import { MasonryDeeplinkAction } from '#types/masonry-driver-node-actions.ts';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { isLinkAction } from '../../utils/is-link-action';
import { useInvokeActionWithLoadingState } from '../../hooks/use-invoke-action-with-loading-state.hook';

type MasonryButtonNodeProperties = Partial<
  Pick<
    React.ComponentProps<typeof Button>,
    | 'destructive'
    | 'disabled'
    | 'hideLabel'
    | 'icon'
    | 'id'
    | 'indicator'
    | 'loading'
    | 'loadingText'
    | 'position'
    | 'priority'
    | 'quiet'
    | 'size'
    | 'textAlign'
    | 'truncateLabel'
    | 'type'
    | 'width'
  >
> & {
  label: string;
};

export type MasonryButtonNodeActions = {
  onClick?: MasonryEngineNodeAction;
};

export type MasonryButtonNode = MasonryEngineNode<
  'button',
  MasonryButtonNodeProperties,
  MasonryButtonNodeActions
>;

export type MasonryButtonRendererProps =
  MasonryEngineNodeRendererProps<MasonryButtonNode>;

export const MasonryButtonRenderer = ({
  label,
  destructive,
  disabled,
  hideLabel,
  icon,
  id,
  indicator,
  loading,
  loadingText,
  onClick,
  position,
  priority,
  quiet,
  size,
  textAlign,
  truncateLabel,
  type,
  width,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryButtonRendererProps) => {
  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction;
  const isLink = isLinkAction(onClickDeeplinkAction);
  const { invokeAction, isLoading: isClickInFlight } =
    useInvokeActionWithLoadingState();

  return (
    <Button
      data-testid={locatorId}
      id={id}
      type={type}
      size={size}
      indicator={indicator}
      width={width}
      position={position}
      destructive={destructive}
      quiet={quiet}
      disabled={disabled || (!isLink && isClickInFlight)}
      loading={loading || (!isLink && isClickInFlight)}
      loadingText={loadingText}
      icon={icon}
      hideLabel={hideLabel}
      truncateLabel={truncateLabel}
      priority={priority}
      textAlign={textAlign}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(isLink && {
        as: 'a',
        href: onClickDeeplinkAction?.payload.url,
      })}
      onClick={
        onClick ? (event) => invokeAction({ event, ...onClick }) : undefined
      }
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      css={css}
    >
      {label}
    </Button>
  );
};
